'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { LandingHeader } from '@/app/components/LandingHeader';
import { Footer } from '@/app/components/Footer';
import { Button } from '@/app/components/ui/Button';
import { Mail, ArrowRight, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { useWillStore } from '@/lib/store';
import { SocialSignInButtons } from '@/app/components/auth/SocialSignInButtons';
import { requestMagicLink } from '@/lib/auth/magic-link';

// Separated component for search params logic
const SignInContent = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isAuthenticated } = useWillStore();
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [canResend, setCanResend] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);
  const [info, setInfo] = useState<string | null>(null);

  // Check for error in URL
  useEffect(() => {
    const errorParam = searchParams.get('error');
    const errorDesc = searchParams.get('error_description');
    if (errorParam) {
      if (errorParam === 'auth_failed') {
        const baseMsg = 'Authentication failed. The link may be expired or invalid. Please try again.';
        setError(errorDesc ? `${baseMsg} (${errorDesc})` : baseMsg);
      } else {
        setError(errorDesc || 'An error occurred during sign in.');
      }
    }
  }, [searchParams]);

  // Email validation
  const isValidEmail = (emailToCheck: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailToCheck) && emailToCheck.length >= 5;
  };

  const buildRedirectUrl = (guestToken?: string) => {
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams();
    params.set('next', '/write-a-will-online');
    if (guestToken) {
      params.set('guest_token', guestToken);
    }
    return `${window.location.origin}/auth/callback?${params.toString()}`;
  };

  const fetchAuthStatus = async (emailAddress: string) => {
    try {
      const res = await fetch('/api/auth/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailAddress }),
      });
      if (!res.ok) return null;
      return (await res.json()) as {
        userExists: boolean;
        staged: boolean;
        guestToken?: string;
        willId?: string;
      };
    } catch (err) {
      console.warn('auth status check failed', err);
      return null;
    }
  };

  // Countdown for resend
  useEffect(() => {
    if (step === 'otp' && !canResend) {
      const timer = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 60;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, canResend]);

  // Listen for auth state changes (user clicks magic link)
  useEffect(() => {
    const supabase = createClient();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // After sign-in, defer all post-auth loading to ClientPage.setupUser
        router.push('/write-a-will-online');
      }
    });

    // Check if already authenticated
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        router.push('/write-a-will-online');
      }
    });

    return () => subscription.unsubscribe();
  }, [router]);

  const sendMagicLink = async (
    emailAddress: string,
    opts?: { allowCreate?: boolean; guestToken?: string; infoMessage?: string }
  ) => {
    if (!isValidEmail(emailAddress)) {
      setError('Please enter a valid email address');
      return { ok: false as const, message: 'Invalid email' };
    }

    try {
      const result = await requestMagicLink({
        email: emailAddress,
        allowCreate: opts?.allowCreate ?? false,
        redirectTo: buildRedirectUrl(opts?.guestToken),
        context: opts?.allowCreate ? 'signup' : 'signin',
      });

      if (!result.ok) {
        setError(result.message);
        return result;
      }

      setStep('otp');
      setCanResend(false);
      setResendCountdown(60);
      setInfo(opts?.infoMessage ?? null);
      setError(null);
      return result;
    } catch (err: any) {
      console.error('Send magic link error:', err);
      setError('Something went wrong. Please try again.');
      return { ok: false as const, message: 'Something went wrong' };
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp || otp.length < 6) return;

    setIsSending(true);
    setError(null);

    try {
      const supabase = createClient();
      const { data: { session }, error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email',
      });

      if (verifyError) throw verifyError;

      if (session?.user) {
        // Let the write-a-will page perform the authoritative bootstrap
        router.push('/write-a-will-online');
        return;
      }

    } catch (err: any) {
      console.error('Error verifying code:', err);
      setError(err.message || 'Invalid code. Please try again.');
      setIsSending(false);
    }
  };

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setIsSending(true);
    const result = await sendMagicLink(email, { allowCreate: false });

    if (!result?.ok && (result as any).code === 'user_not_found') {
      const status = await fetchAuthStatus(email);

      if (status?.staged && status.guestToken) {
        await sendMagicLink(email, {
          allowCreate: true,
          guestToken: status.guestToken,
          infoMessage: 'We found your saved draft. Check your email to finish signing in.',
        });
      } else {
        setError("We couldn't find an account with that email.");
      }
    }

    setIsSending(false);
  };

  const handleResend = async () => {
    setError(null);
    setInfo(null);
    setIsResending(true);
    const result = await sendMagicLink(email, { allowCreate: false });

    if (!result?.ok && (result as any).code === 'user_not_found') {
      const status = await fetchAuthStatus(email);
      if (status?.staged && status.guestToken) {
        await sendMagicLink(email, {
          allowCreate: true,
          guestToken: status.guestToken,
          infoMessage: 'We found your saved draft. Check your email to finish signing in.',
        });
      } else {
        setError("We couldn't find an account with that email.");
      }
    }

    setIsResending(false);
  };

  // If already authenticated, redirect
  useEffect(() => {
    if (isAuthenticated) {
      router.push('/write-a-will-online');
    }
  }, [isAuthenticated, router]);

  // Show nothing while redirecting
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="w-full max-w-md">
      {step === 'otp' ? (
        <div className="bg-white rounded-2xl shadow-sm border border-foreground/10 p-8 sm:p-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-display text-foreground mb-2">
            Check your email
          </h1>
          <p className="text-muted-foreground mb-2">
            We've sent a 6-digit code to
          </p>
          <p className="font-medium text-foreground mb-6">
            {email}
          </p>
          {info && (
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-left">
              {info}
            </p>
          )}

          <form onSubmit={handleVerifyOtp} className="space-y-4 mb-6">
            <div className="space-y-2">
              <input
                type="text"
                placeholder="123456"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full text-center text-2xl tracking-[0.5em] font-mono h-14 rounded-lg border border-foreground/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                maxLength={6}
                required
                autoFocus
              />
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2 text-left">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={isSending || otp.length < 6}
              className="w-full"
            >
              {isSending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify Code"
              )}
            </Button>
          </form>

          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Didn't receive the code?
            </p>
            {canResend ? (
              <Button variant="outline" onClick={handleResend} disabled={isResending} className="w-full">
                {isResending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Resend code'
                )}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Resend in {resendCountdown}s
              </p>
            )}
            <button
              onClick={() => {
                setStep('email');
                setOtp('');
                setError(null);
              }}
              className="text-sm text-primary hover:underline"
            >
              Use a different email
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-foreground/10 p-8 sm:p-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-display text-foreground mb-2">
              Welcome back
            </h1>
            <p className="text-muted-foreground">
              Sign in to view or edit your Will
            </p>
          </div>

          <form onSubmit={handleSendMagicLink} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="w-full px-4 py-3 rounded-lg border border-foreground/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                required
              />
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={isSending || !email}
              className="w-full"
            >
              {isSending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send sign in code
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-muted-foreground">
                Or
              </span>
            </div>
          </div>

          <SocialSignInButtons className="mb-6" />

          <div className="mt-6 pt-6 border-t border-foreground/10 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <a href="/write-a-will-online" className="text-primary hover:underline font-medium">
                Create your Will
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default function SignInPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LandingHeader />
      <main className="flex-1 flex items-center justify-center px-4 py-16 sm:py-24">
        <Suspense fallback={<div className="flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
          <SignInContent />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
